export type ElasticLogLevel = 'debug' | 'info' | 'warning' | 'error';
export type ElasticLogGroup = 'DEBUG' | 'INFO' | 'WARNING' | 'PROD';

interface LoggerOptions {
  group?: ElasticLogGroup;
}
class ElasticLogger {
  private static instance: ElasticLogger;
  private levels: ElasticLogLevel[];
  private domain = '';
  private opId = '';
  private isServer: boolean | undefined = undefined;

  private constructor(options?: LoggerOptions) {
    this.levels = this.resolveLogLevels(options?.group || 'DEBUG');
  }

  static getInstance(options?: LoggerOptions): ElasticLogger {
    if (!ElasticLogger.instance) {
      ElasticLogger.instance = new ElasticLogger(options);
    }
    return ElasticLogger.instance;
  }

  init(isServer = false, domain?: string, opId?: string | null): this {
    this.isServer = isServer;
    this.domain = domain || (typeof window !== 'undefined' ? window.location.origin : '');
    this.opId = opId || (typeof window !== 'undefined' ? window.opId : '');
    return this;
  }

  private resolveLogLevels(group: ElasticLogGroup): ElasticLogLevel[] {
    const groups: Record<ElasticLogGroup, ElasticLogLevel[]> = {
      DEBUG: ['debug', 'info', 'warning', 'error'],
      INFO: ['info', 'warning', 'error'],
      WARNING: ['info', 'warning', 'error'],
      PROD: ['info', 'error'],
    };

    return groups[group].filter((level): level is ElasticLogLevel =>
      ['debug', 'info', 'warning', 'error'].includes(level)
    );
  }

  private getCurrentTime(): number {
    return Date.now();
  }

  private formatMeta(meta: unknown, depth = 0): unknown {
    if (depth > 3) return '[Depth Limit Exceeded]';

    if (meta instanceof Error) {
      return { message: meta.message, stack: meta.stack };
    }

    if (meta && typeof meta === 'object') {
      return Object.entries(meta).reduce<Record<string, unknown>>((formatted, [key, val]) => {
        formatted[key] = this.formatMeta(val, depth + 1);
        return formatted;
      }, {});
    }

    return meta;
  }

  private async sendLog(
    type: ElasticLogLevel,
    message: string,
    service: string,
    meta?: unknown,
    indexedData?: Record<string, unknown>
  ): Promise<void> {
    try {
      const payload = {
        level: type.toUpperCase(),
        platform: 'PPF',
        service,
        title: message,
        origin: this.domain,
        opId: this.opId,
        meta: this.formatMeta(meta),
        timestamp: this.getCurrentTime(),
        env: process.env.NEXT_PUBLIC_ENV || 'DEV',
        instance: process.env.WEBSITE_INSTANCE_ID || 'none',
        ...indexedData,
      };

      const headers = new Headers();
      headers.append('Content-Type', 'application/json');

      const url = this.isServer ? `${process.env.NEXT_PUBLIC_ELASTIC_LOG_URL}` : `${this.domain}/api/logger`;

      const requestOptions: RequestInit = {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      };

      void fetch(url, requestOptions);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('[BO] Failed to send log:', {
        error: e,
        title: message,
        service,
      });
    }
  }

  log(
    type: ElasticLogLevel,
    service: string,
    message: string,
    meta?: unknown,
    indexedData?: Record<string, unknown>
  ): void {
    if (this.levels.includes(type)) {
      void this.sendLog(type, message, service, meta, indexedData);
    }
  }

  debug(service: string, message: string, meta?: unknown, indexedData?: Record<string, unknown>): void {
    this.log('debug', service, message, meta, indexedData);
  }

  info(service: string, message: string, meta?: unknown, indexedData?: Record<string, unknown>): void {
    this.log('info', service, message, meta, indexedData);
  }

  warning(service: string, message: string, meta?: unknown, indexedData?: Record<string, unknown>): void {
    this.log('warning', service, message, meta, indexedData);
  }

  error(service: string, message: string, meta?: unknown, indexedData?: Record<string, unknown>): void {
    this.log('error', service, message, meta, indexedData);
  }

  setGroup(group: ElasticLogGroup): void {
    this.levels = this.resolveLogLevels(group);
  }
}

const elasticLogger = ElasticLogger.getInstance();
export default elasticLogger;
